import React, { useState } from 'react'
import COLORS from '../styles/colors'
import Loader from 'react-loader-spinner'

import 'react-loader-spinner/dist/loader/css/react-spinner-loader.css'
import { useCommonStyles } from '../styles/globalStyles'
import FacebookButton from './FacebookButton'
import { createUseStyles } from 'react-jss'
import GeneralInput from './GeneralInput'
import { filterString } from 'helpers/cleanup'
import GeneralInputMask from './GeneralInputMask'
import { cpfMask, phoneMask } from 'constants/masks'

interface Props {
  form: {
    name: string
    password: string
    email: string
    phone?: string
    cpf?: string
  }
  updateField: (event: any) => void
  onSubmit: () => Promise<void>
  headline: string
  subheadline: string
  submitButtonText: string
  onClickFacebook: (e: any) => void

  isLoading: boolean
  hasSubmitted: boolean
  sucessMessage: string

  hasCpf?: boolean
  /**
   * useStyles class name
   */
  containerStyle?: string
}

const FORM_TRANSLATION_MAP = {
  name: 'Nome completo',
  password: 'Senha',
  phone: 'Telefone',
  email: 'Email',
  cpf: 'CPF',
}

const SignupForm = (props: Props) => {
  const classes = useStyles(props)
  const commonClasses = useCommonStyles(props)
  const [errors, setErrors] = useState({
    name: '',
    password: '',
    email: '',
    phone: '',
    cpf: '',
  })

  const setErrorType = (type: keyof Props['form'], text: string) => {
    setErrors((prev) => ({ ...prev, [type]: text }))
  }

  const handleChange = (e) => {
    e.persist()

    setErrors((prev) => ({ ...prev, [e.target.name]: '' }))
    props.updateField(e)
  }

  const checkIfHasErrors = () => {
    let hasError = false
    Object.keys(props.form).forEach((key) => {
      const type = key as keyof Props['form']
      if (!props.form[type]) {
        setErrorType(type, `${FORM_TRANSLATION_MAP[type]} é um campo obrigatório`)
        hasError = true
      }
    })

    if (props.form.email && props.form.email.indexOf('@') === -1) {
      setErrorType('email', 'Por favor insira um Email válido')
      hasError = true
    }

    if (props.form.phone && filterString(props.form.phone, ['(', ')', '+', '_']).length < 11) {
      setErrorType('phone', 'Por favor insira um Telefone válido')
      hasError = true
    }

    if (props.form.cpf && filterString(props.form.cpf, ['.', '-', '_']).length < 11) {
      setErrorType('cpf', 'Por favor insira um CPF válido')
      hasError = true
    }

    if (props.form.password && props.form.password.length < 8) {
      setErrorType('password', 'Senha precisa ter no mínimo 8 caracteres')
      hasError = true
    }

    return hasError
  }

  const handleSubmit = (e: any) => {
    e.preventDefault()
    const hasError = checkIfHasErrors()

    if (!hasError) {
      props.onSubmit()
    }
  }

  return (
    <div className={`${classes.form} ${props.containerStyle || ''}`}>
      <h1 className={classes.headline}>{props.headline}</h1>
      <p className={classes.subheadline}>{props.subheadline}</p>
      {!props.hasSubmitted && !props.isLoading && (
        <form onSubmit={handleSubmit} className={classes.inputsContainer} method='post'>
          {/* ################ FORM ################ */}
          <GeneralInput
            className={classes.input}
            inputClassName={classes.capitalizedInput}
            value={props.form.name}
            label='Nome'
            type='name'
            name='name'
            handleChange={handleChange}
            error={errors.name}
          />
          <GeneralInput
            className={classes.input}
            value={props.form.email}
            label='Email'
            type='email'
            name='email'
            handleChange={handleChange}
            error={errors.email}
          />
          <GeneralInput
            className={classes.input}
            value={props.form.password}
            label='Senha'
            type='password'
            name='password'
            handleChange={handleChange}
            error={errors.password}
          />

          {Boolean(props.hasCpf) && (
            <GeneralInputMask
              value={props.form.cpf}
              className={classes.input}
              label='CPF'
              type='cpf'
              name='cpf'
              mask={cpfMask}
              handleChange={handleChange}
              error={errors.cpf}
            />
          )}

          <GeneralInputMask
            value={props.form.phone}
            className={classes.input}
            label='Telefone'
            type='phone'
            name='phone'
            mask={phoneMask}
            handleChange={handleChange}
            error={errors.phone}
          />
          <button className={`${commonClasses.buttonStyle} ${classes.button}`} type='submit'>
            {props.submitButtonText}
          </button>

          {/* ################ FACEBOOK BUTTON ################ */}
          <p className={classes.orText}>ou</p>
          <FacebookButton
            className={`${commonClasses.buttonStyle} ${classes.facebookButton}`}
            onClick={props.onClickFacebook}
          />
        </form>
      )}
      {props.isLoading && (
        <Loader type='Oval' color='#00BFFF' height={60} width={60} style={{ marginTop: '50px' }} />
      )}
      {props.hasSubmitted && !props.isLoading && (
        <p className={classes.successMessage}>{props.sucessMessage}</p>
      )}
    </div>
  )
}

SignupForm.default = {
  hasCpf: false,
  isLoading: false,
  hasSubmitted: false,
}

const useStyles = createUseStyles({
  form: (props: Props) => ({
    padding: 24,
    paddingBottom: 50,
    backgroundColor: COLORS.greyscale.white,
    borderRadius: 6,
    color: COLORS.greyscale.black,
  }),
  headline: {
    fontWeight: 700,
    fontSize: 21,
    marginBottom: 8,
    textAlign: 'center',
  },
  subheadline: {
    fontSize: 18,
    marginBottom: 16,
    textAlign: 'center',
  },
  orText: {
    fontSize: 18,
    margin: '13px 0',
    textAlign: 'center',
  },
  inputsContainer: {
    marginTop: 21,
  },
  input: {
    textAlign: 'left',
  },
  capitalizedInput: {
    textTransform: 'capitalize',
  },
  button: {
    backgroundColor: COLORS.brand.primary,
    color: COLORS.greyscale.white,
    marginTop: 10,
  },
  facebookButton: {
    color: COLORS.greyscale.white,
    backgroundColor: COLORS.brand.primary,
  },
  successMessage: {
    color: COLORS.greyscale.white,
    fontSize: 22,
    textAlign: 'center',
    margin: '50px 0 16px',
  },
})

export default SignupForm
